import { type ReactElement } from "react";
import ReactMarkdown from "react-markdown";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { cn } from "../utils/cn";

/** Forgiving prep step for content recruiters type by hand:
 * - Promote `-foo` / `*foo` at line-start to `- foo` / `* foo` so the
 *   common "forgot the space" typo still becomes a list.
 * - Strip trailing whitespace per line so the line-start regex is reliable. */
function normalizeMarkdown(src: string): string {
  return src
    .split("\n")
    .map((line) => line.replace(/^([-*])(\S)/, "$1 $2"))
    .join("\n");
}

export interface MarkdownProps {
  children: string;
  className?: string;
  /** Empty-state text to render when `children` is blank. Default: nothing. */
  empty?: string;
}

/** Restrict the sanitizer to a safe subset + allowlist mailto/https URIs.
 * We never render raw HTML, and we forbid all unknown schemes — anything
 * the recruiter pastes goes through here. */
const SCHEMA: typeof defaultSchema = {
  ...defaultSchema,
  protocols: {
    ...defaultSchema.protocols,
    href: ["http", "https", "mailto"],
    src: ["http", "https"],
  },
  tagNames: [...(defaultSchema.tagNames ?? [])].filter(
    (t) => t !== "img",
  ),
};

/** Renders markdown with GFM (tables, strikethrough, autolinks, task
 * lists). Sanitized — notes are user input. Tailwind-themed so content
 * reads consistently whether it's a single paragraph or a numbered list.
 *
 * Paired with `<Textarea>` for the edit side of the view-until-click
 * pattern used across NotesCard / InterviewCard / RecommendationCard. */
export function Markdown({ children, className, empty }: MarkdownProps): ReactElement {
  if (!children || !children.trim()) {
    return (
      <p className={cn("m-0 text-sm italic text-text-tertiary", className)}>
        {empty ?? ""}
      </p>
    );
  }
  return (
    <div className={cn(MD_CLASSES, className)} data-testid="markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[[rehypeSanitize, SCHEMA]]}
      >
        {normalizeMarkdown(children)}
      </ReactMarkdown>
    </div>
  );
}

// Tailwind utility stack that styles the rendered markdown tree. Kept
// terse — matches the prototype's body copy and the rest of the app's
// typography tokens.
const MD_CLASSES = [
  "text-sm leading-relaxed text-text-primary",
  // Paragraphs + leading
  "[&>p]:m-0 [&>p+p]:mt-3",
  // Headings
  "[&>h1]:mt-4 [&>h1]:mb-2 [&>h1]:text-lg [&>h1]:font-semibold",
  "[&>h2]:mt-4 [&>h2]:mb-2 [&>h2]:text-base [&>h2]:font-semibold",
  "[&>h3]:mt-3 [&>h3]:mb-2 [&>h3]:text-sm [&>h3]:font-semibold",
  // Lists
  "[&>ul]:m-0 [&>ul]:mt-1 [&>ul]:pl-5 [&>ul]:list-disc",
  "[&>ol]:m-0 [&>ol]:mt-1 [&>ol]:pl-5 [&>ol]:list-decimal",
  "[&_li+li]:mt-1",
  // Emphasis
  "[&_strong]:font-semibold",
  "[&_em]:italic",
  // Code
  "[&_code]:rounded-sm [&_code]:bg-neutral-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs",
  "[&>pre]:mt-2 [&>pre]:overflow-auto [&>pre]:rounded-md [&>pre]:bg-neutral-100 [&>pre]:p-3",
  "[&>pre>code]:bg-transparent [&>pre>code]:p-0",
  // Links
  "[&_a]:text-primary-600 [&_a:hover]:underline",
  // Blockquote
  "[&>blockquote]:mt-2 [&>blockquote]:border-l-2 [&>blockquote]:border-neutral-300 [&>blockquote]:pl-3 [&>blockquote]:italic [&>blockquote]:text-text-secondary",
  // Tables (GFM)
  "[&_table]:mt-3 [&_table]:w-full [&_table]:border-collapse",
  "[&_th]:border [&_th]:border-border-default [&_th]:bg-neutral-50 [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold",
  "[&_td]:border [&_td]:border-border-default [&_td]:px-2 [&_td]:py-1.5 [&_td]:text-xs",
].join(" ");
